import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Circles } from 'react-loader-spinner';
import Cookies from 'js-cookie';
import Modal from 'react-bootstrap/Modal';
import getApplicationId from './api/getApplicationId';
import {
  FaHome, FaUsers, FaCalendarDay, FaListAlt,
  FaSignOutAlt, FaPlus, FaChevronRight,
} from 'react-icons/fa';
import base_url from './api/Config';
import authFetch from './api/authFetch';

/* ── status map ──────────────────────────────── */
const statusMap = {
  1:  { label: 'Registered',         path: '/Address'           },
  2:  { label: 'Address Complete',   path: '/CreditCheck'       },
  3:  { label: 'CIBIL Complete',     path: '/BankDetail'        },
  4:  { label: 'Banking Complete',   path: '/Employmentdetails' },
  5:  { label: 'Employment Complete',path: '/Nominee'           },
  6:  { label: 'Nominee Complete',   path: '/Reference'         },
  7:  { label: 'Reference Complete', path: '/DocumentUpload'    },
  8:  { label: 'Documents Uploaded',  path: '/ProductTypes'      },
  9:  { label: 'Loan Under Process',  path: null                 },
  10: { label: 'Loan Disbursed',     path: '/CustomerLoanList'  },
  15: { label: 'Loan Rejected',      path: null                 },
};
const sColor = (c) => c === 10 ? '#11c232' : c === 15 ? '#e53935' : '#1d2e77';

const todayFull = () =>
  new Date().toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long', year:'numeric' });

const TAB_LABELS = { home:'Dashboard', loan:'Customers', today:"Today's Repayments", totalemi:'Total Repayments' };

/* ── shared sub-views ────────────────────────── */
const Loader = () => (
  <div style={{ display:'flex', justifyContent:'center', paddingTop:60 }}>
    <Circles height={60} width={60} color="#1d2e77" />
  </div>
);

const Empty = ({ msg, sub }) => (
  <div style={{ background:'#fff', borderRadius:12, padding:32, textAlign:'center', boxShadow:'0 1px 6px rgba(0,0,0,0.08)', marginBottom:16 }}>
    <p style={{ fontWeight:700, fontSize:15, color:'#555', marginBottom:6 }}>{msg}</p>
    {sub && <p style={{ fontSize:13, color:'#888', margin:0 }}>{sub}</p>}
  </div>
);

const Err = ({ msg, onRetry }) => (
  <div style={{ background:'#fff', borderRadius:12, padding:24, textAlign:'center', boxShadow:'0 1px 6px rgba(0,0,0,0.08)', marginBottom:16 }}>
    <p style={{ color:'#e53935', fontWeight:700, marginBottom:8 }}>Error</p>
    <p style={{ fontSize:13, color:'#666', marginBottom:16 }}>{msg}</p>
    <button onClick={onRetry} style={{ background:'#1d2e77', color:'#fff', border:'none', borderRadius:8, padding:'8px 24px', fontWeight:700, cursor:'pointer' }}>Retry</button>
  </div>
);

const InfoRow = ({ label, value, last }) => (
  <div style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom: last ? 'none' : '1px solid #f0f0f0', fontSize:14 }}>
    <span style={{ color:'#888' }}>{label}</span>
    <span style={{ fontWeight:600 }}>{value}</span>
  </div>
);

/* ── main component ──────────────────────────── */
const Home = () => {
  const [tab, setTab]           = useState('home');
  const [showLogout, setShowLogout] = useState(false);

  /* loan tab */
  const [customers,  setCustomers]  = useState([]);
  const [filtered,   setFiltered]   = useState([]);
  const [qLoan,      setQLoan]      = useState('');
  const [lLoaded, setLLoaded] = useState(false);
  const [lLoading,   setLLoading]   = useState(false);
  const [lErr,       setLErr]       = useState(null);

  /* today tab */
  const [todayList,  setTodayList]  = useState([]);
  const [tLoaded, setTLoaded] = useState(false);
  const [tLoading,   setTLoading]   = useState(false);
  const [tErr,       setTErr]       = useState(null);

  /* total tab */
  const [totalList,  setTotalList]  = useState([]);
  const [totalFilt,  setTotalFilt]  = useState([]);
  const [qTotal,     setQTotal]     = useState('');
  const [aLoaded, setALoaded] = useState(false);
  const [aLoading,   setALoading]   = useState(false);
  const [aErr,       setAErr]       = useState(null);
  const [processModal, setProcessModal] = useState(false);
  const [rejectModal,  setRejectModal]  = useState(false);

  const navigate  = useNavigate();
  const adminId   = localStorage.getItem('adminId') || 'Admin';
  const fintechId = localStorage.getItem('fintechId');

  /* auth guard */
  useEffect(() => { if (!Cookies.get('authToken')) navigate('/'); }, [navigate]);

  /* ── fetchers ─────────────────────────── */
  const loadLoan = useCallback(async () => {
    if (lLoaded) return;
    setLLoading(true); setLErr(null);
    try {
      const r = await authFetch(`${base_url}/api/customer/fintechIdByCustomerList?fintechId=${fintechId}`);
      if (!r.ok) throw new Error('Failed to load customers');
      const data = await r.json();

      const ws = await Promise.all(data.map(async (c) => {
        try {
          const sr = await authFetch(`${base_url}/api/lead/customerStatus?customerId=${c.id}`);
          const { customerStatus } = await sr.json();
          const code = parseInt(customerStatus || 1);
          return { ...c, statusCode: code, statusLabel: (statusMap[code] || { label:'Unknown' }).label };
        } catch { return { ...c, statusCode:0, statusLabel:'Unknown' }; }
      }));
      const sorted = ws.sort((a, b) => b.id - a.id);
      setCustomers(sorted); setFiltered(sorted); setLLoaded(true);
    } catch (e) { setLErr(e.message); }
    finally { setLLoading(false); }
  }, [fintechId, lLoaded]);

  const loadToday = useCallback(async () => {
    if (tLoaded) return;
    setTLoading(true); setTErr(null);
    try {
      const r = await authFetch(`${base_url}/api/repayment/fintechTodayCollection?fintechId=${fintechId}`);
      if (!r.ok) throw new Error("Could not load today's repayments");
      const data = await r.json();
      setTodayList(Array.isArray(data) ? data : []); setTLoaded(true);
    } catch (e) { setTErr(e.message); }
    finally { setTLoading(false); }
  }, [fintechId, tLoaded]);

  const loadTotal = useCallback(async () => {
    if (aLoaded) return;
    setALoading(true); setAErr(null);
    try {
      const r = await authFetch(`${base_url}/api/repayment/fintechAllRepayments?fintechId=${fintechId}`);
      if (!r.ok) throw new Error('Could not load total repayments');
      const data = await r.json();
      const list = Array.isArray(data) ? data : [];
      setTotalList(list); setTotalFilt(list); setALoaded(true);
    } catch (e) { setAErr(e.message); }
    finally { setALoading(false); }
  }, [fintechId, aLoaded]);

  useEffect(() => { loadLoan(); }, [loadLoan]);

  useEffect(() => {
    if (tab === 'today')    loadToday();
    if (tab === 'totalemi') loadTotal();
  }, [tab, loadToday, loadTotal]);

  /* search filters */
  useEffect(() => {
    const q = qLoan.toLowerCase().trim();
    setFiltered(q ? customers.filter(c =>
      (c.borrowerName||'').toLowerCase().includes(q) ||
      (c.mobileNumber||'').includes(q) ||
      String(c.id||'').includes(q)
    ) : customers);
  }, [qLoan, customers]);

  useEffect(() => {
    const q = qTotal.toLowerCase().trim();
    setTotalFilt(q ? totalList.filter(r =>
      (r.borrowerName||r.customerName||'').toLowerCase().includes(q) ||
      (r.mobileNo||r.mobileNumber||'').includes(q) ||
      String(r.loanId||r.id||'').includes(q)
    ) : totalList);
  }, [qTotal, totalList]);

  /* customer click */
  const clickCustomer = async (c) => {
    try {
      const r = await authFetch(`${base_url}/api/lead/customerStatus?customerId=${c.id}`);
      if (!r.ok) throw new Error();
      const { customerStatus } = await r.json();
      const code = parseInt(customerStatus || 1);
      if (code === 9)  { setProcessModal(true); return; }
      if (code === 15) { setRejectModal(true); return; }
      if (code === 10) { navigate(`/CustomerLoanList?customerId=${c.id}`); return; }
      const info = statusMap[code] || { path:'/Register' };
      const applicationId = await getApplicationId(c.id);
      navigate(`${info.path}?applicationId=${applicationId}&customerId=${c.id}`);
    } catch { alert('Could not track customer status.'); }
  };

  const logout = () => { Cookies.remove('authToken'); localStorage.clear(); navigate('/'); };

  const hour = new Date().getHours();
  const greet = hour < 12 ? 'Good Morning' : hour < 17 ? 'Good Afternoon' : 'Good Evening';

  /* EMI status badge */
  const badge = (item) => {
    const pending = item.emiStatus === 0 || item.status === 'Pending' || item.emiStatus === 'PENDING';
    return pending
      ? <span style={{ background:'#fff3e0', color:'#e65100', borderRadius:20, padding:'3px 10px', fontSize:12, fontWeight:700 }}>Pending</span>
      : <span style={{ background:'#e8f5e9', color:'#2e7d32', borderRadius:20, padding:'3px 10px', fontSize:12, fontWeight:700 }}>Paid</span>;
  };

  /* ── nav tabs config ──────────────────── */
  const TABS = [
    { key:'home',     Icon:FaHome,       label:'Home'    },
    { key:'loan',     Icon:FaUsers,      label:'Loan'    },
    { key:'today',    Icon:FaCalendarDay,label:'Today'   },
    { key:'totalemi', Icon:FaListAlt,    label:'All EMI' },
  ];

  /* ──────────────────────────────────────────
     LAYOUT: full-height flex column
     header (fixed height) → scrollable content → bottom nav (fixed height)
  ─────────────────────────────────────────── */
  return (
    <div style={{
      height: '100vh',
      maxWidth: 480,
      margin: '0 auto',
      display: 'flex',
      flexDirection: 'column',
      background: '#f0f2f5',
      overflow: 'hidden',
    }}>

      {/* ═══ TOP HEADER ═══════════════════════════ */}
      <div style={{
        flexShrink: 0,
        background: '#1d2e77',
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
        zIndex: 10,
      }}>
        <div>
          <p style={{ color:'rgba(255,255,255,0.6)', fontSize:11, margin:0, fontWeight:500 }}>
            Ujjwal Srijan
          </p>
          <p style={{ color:'#fff', fontWeight:700, fontSize:18, margin:0 }}>
            {TAB_LABELS[tab]}
          </p>
        </div>

        {/* LOGOUT BUTTON */}
        <button
          onClick={() => setShowLogout(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            background: '#e53935',
            border: 'none',
            borderRadius: 10,
            color: '#fff',
            padding: '9px 16px',
            fontWeight: 700,
            fontSize: 14,
            cursor: 'pointer',
            letterSpacing: 0.3,
          }}
        >
          <FaSignOutAlt size={14} />
          Logout
        </button>
      </div>

      {/* ═══ SCROLLABLE CONTENT ════════════════════ */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>

        {/* ─── HOME TAB ─────────────────────── */}
        {tab === 'home' && (
          <>
            {/* Welcome banner */}
            <div style={{
              background:'linear-gradient(135deg,#1d2e77 0%,#2a3f99 100%)',
              borderRadius:14, padding:'20px', marginBottom:16,
              boxShadow:'0 4px 18px rgba(29,46,119,0.28)',
            }}>
              <p style={{ color:'rgba(255,255,255,0.7)', fontSize:13, margin:'0 0 3px' }}>{greet} 👋</p>
              <p style={{ color:'#fff', fontWeight:700, fontSize:22, margin:'0 0 16px' }}>{adminId}</p>
              <div style={{ display:'flex', gap:10 }}>
                <div style={{ flex:1, background:'rgba(255,255,255,0.13)', borderRadius:10, padding:'10px 14px' }}>
                  <p style={{ color:'rgba(255,255,255,0.65)', fontSize:11, margin:'0 0 3px' }}>Total Customers</p>
                  <p style={{ color:'#fff', fontWeight:700, fontSize:26, margin:0 }}>
                    {lLoaded ? customers.length : '—'}
                  </p>
                </div>
                <div style={{ flex:1, background:'rgba(17,194,50,0.2)', borderRadius:10, padding:'10px 14px' }}>
                  <p style={{ color:'rgba(255,255,255,0.65)', fontSize:11, margin:'0 0 3px' }}>Disbursed</p>
                  <p style={{ color:'#11c232', fontWeight:700, fontSize:26, margin:0 }}>
                    {lLoaded ? customers.filter(c => c.statusCode === 10).length : '—'}
                  </p>
                </div>
              </div>
            </div>

            {/* Quick actions */}
            <div style={{ background:'#fff', borderRadius:12, padding:16, marginBottom:16, boxShadow:'0 1px 6px rgba(0,0,0,0.08)' }}>
              <p style={{ fontWeight:700, color:'#1d2e77', fontSize:14, marginBottom:12, paddingBottom:6, borderBottom:'2px solid #e8eaf6' }}>
                Quick Actions
              </p>
              <button
                onClick={() => navigate('/Register')}
                style={{
                  width:'100%', height:50, background:'#1d2e77', color:'#fff',
                  border:'none', borderRadius:10, fontSize:15, fontWeight:700,
                  cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8, marginBottom:10,
                }}
              >
                <FaPlus /> Add New Customer
              </button>
              <button
                onClick={() => setTab('loan')}
                style={{
                  width:'100%', height:48, background:'#f0f3ff', color:'#1d2e77',
                  border:'1.5px solid #c5cae9', borderRadius:10, fontSize:14,
                  fontWeight:700, cursor:'pointer',
                  display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 16px',
                }}
              >
                <span>View All Customers</span> <FaChevronRight size={13} />
              </button>
            </div>

            {/* Today shortcuts */}
            <div style={{ background:'#fff', borderRadius:12, padding:16, marginBottom:16, boxShadow:'0 1px 6px rgba(0,0,0,0.08)' }}>
              <p style={{ fontWeight:700, color:'#1d2e77', fontSize:14, marginBottom:12, paddingBottom:6, borderBottom:'2px solid #e8eaf6' }}>
                Today — {todayFull()}
              </p>
              <div onClick={() => setTab('today')} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid #f0f0f0', cursor:'pointer', fontSize:14 }}>
                <span>Today's Repayments</span>
                <span style={{ color:'#1d2e77', fontWeight:700, display:'flex', alignItems:'center', gap:4 }}>View <FaChevronRight size={11}/></span>
              </div>
              <div onClick={() => setTab('totalemi')} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', cursor:'pointer', fontSize:14 }}>
                <span>Total Repayments</span>
                <span style={{ color:'#1d2e77', fontWeight:700, display:'flex', alignItems:'center', gap:4 }}>View <FaChevronRight size={11}/></span>
              </div>
            </div>
          </>
        )}

        {/* ─── LOAN TAB ─────────────────────── */}
        {tab === 'loan' && (
          <>
            <div style={{ display:'flex', gap:8, marginBottom:12 }}>
              <input
                type="text"
                placeholder="Search name, mobile, ID..."
                value={qLoan}
                onChange={e => setQLoan(e.target.value)}
                style={{ flex:1, height:46, border:'1.5px solid #e0e0e0', borderRadius:8, padding:'0 12px', fontSize:15, outline:'none' }}
              />
              <button
                onClick={() => navigate('/Register')}
                style={{ width:46, height:46, background:'#1d2e77', color:'#fff', border:'none', borderRadius:10, fontSize:18, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}
              >
                <FaPlus />
              </button>
            </div>

            {lLoading ? <Loader /> :
             lErr      ? <Err msg={lErr} onRetry={() => { setLLoaded(false); loadLoan(); }} /> :
             filtered.length === 0 ? (
               <Empty msg="No customers found" sub={qLoan ? 'Try a different search.' : 'Add your first customer.'} />
             ) : filtered.map((c, i) => (
               <div key={i} onClick={() => clickCustomer(c)} style={{ background:'#fff', borderRadius:12, padding:14, marginBottom:10, boxShadow:'0 1px 6px rgba(0,0,0,0.08)', cursor:'pointer' }}>
                 <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                   <p style={{ fontWeight:700, fontSize:15, color:'#1d2e77', margin:0 }}>{c.borrowerName}</p>
                   <span style={{ background:sColor(c.statusCode)+'18', color:sColor(c.statusCode), borderRadius:20, padding:'3px 10px', fontSize:12, fontWeight:700 }}>{c.statusLabel}</span>
                 </div>
                 <InfoRow label="Mobile" value={c.mobileNumber} />
                 <InfoRow label="Customer ID" value={c.id} last />
               </div>
             ))}
          </>
        )}

        {/* ─── TODAY TAB ────────────────────── */}
        {tab === 'today' && (
          <>
            <div style={{ background:'linear-gradient(135deg,#1d2e77 0%,#2a3f99 100%)', borderRadius:14, padding:'16px 20px', marginBottom:16, boxShadow:'0 4px 14px rgba(29,46,119,0.2)' }}>
              <p style={{ color:'rgba(255,255,255,0.7)', fontSize:12, margin:'0 0 3px' }}>Today's Collections</p>
              <p style={{ color:'#fff', fontWeight:700, fontSize:14, margin:0 }}>{todayFull()}</p>
            </div>

            {tLoading ? <Loader /> :
             tErr      ? <Err msg={tErr} onRetry={() => { setTLoaded(false); loadToday(); }} /> :
             todayList.length === 0 ? (
               <Empty msg="No repayments due today" sub="All EMIs are up to date." />
             ) : todayList.map((item, i) => (
               <div key={i} style={{ background:'#fff', borderRadius:12, padding:14, marginBottom:10, boxShadow:'0 1px 6px rgba(0,0,0,0.08)' }}>
                 <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                   <p style={{ fontWeight:700, fontSize:15, color:'#1d2e77', margin:0 }}>
                     {item.borrowerName || item.customerName || `EMI #${item.emiNumber || i+1}`}
                   </p>
                   {badge(item)}
                 </div>
                 <InfoRow label="EMI Amount" value={`₹${item.emiAmount}`} />
                 {item.dueDate && <InfoRow label="Due Date" value={item.dueDate} />}
                 {(item.loanId || item.applicationId) && <InfoRow label="Loan ID" value={item.loanId || item.applicationId} last />}
               </div>
             ))}
          </>
        )}

        {/* ─── TOTAL EMI TAB ─────────────────── */}
        {tab === 'totalemi' && (
          <>
            <input
              type="text"
              placeholder="Search name, mobile, loan ID..."
              value={qTotal}
              onChange={e => setQTotal(e.target.value)}
              style={{ width:'100%', height:46, border:'1.5px solid #e0e0e0', borderRadius:8, padding:'0 12px', fontSize:15, outline:'none', marginBottom:12 }}
            />

            {/* summary strip */}
            {aLoaded && totalList.length > 0 && (
              <div style={{ display:'flex', gap:10, marginBottom:14 }}>
                {[
                  { label:'Total',   val: totalList.length,                                                              color:'#1d2e77' },
                  { label:'Pending', val: totalList.filter(r => r.emiStatus===0||r.status==='Pending').length,           color:'#e65100' },
                  { label:'Paid',    val: totalList.filter(r => r.emiStatus!==0&&r.status!=='Pending').length,           color:'#11c232' },
                ].map(s => (
                  <div key={s.label} style={{ flex:1, background:'#fff', borderRadius:10, padding:'10px 8px', textAlign:'center', boxShadow:'0 1px 5px rgba(0,0,0,0.07)' }}>
                    <p style={{ fontSize:11, color:'#888', margin:'0 0 2px' }}>{s.label}</p>
                    <p style={{ fontWeight:700, fontSize:22, color:s.color, margin:0 }}>{s.val}</p>
                  </div>
                ))}
              </div>
            )}

            {aLoading ? <Loader /> :
             aErr      ? <Err msg={aErr} onRetry={() => { setALoaded(false); loadTotal(); }} /> :
             totalFilt.length === 0 ? (
               <Empty msg="No repayments found" sub={qTotal ? 'Try a different search.' : 'No repayment data available.'} />
             ) : totalFilt.map((item, i) => (
               <div key={i} style={{ background:'#fff', borderRadius:12, padding:14, marginBottom:10, boxShadow:'0 1px 6px rgba(0,0,0,0.08)' }}>
                 <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                   <p style={{ fontWeight:700, fontSize:14, color:'#1d2e77', margin:0 }}>
                     {item.borrowerName || item.customerName || `EMI #${item.emiNumber || i+1}`}
                   </p>
                   {badge(item)}
                 </div>
                 <InfoRow label="EMI Amount" value={`₹${item.emiAmount}`} />
                 {item.dueDate && <InfoRow label="Due Date" value={item.dueDate} />}
                 {(item.loanId || item.applicationId) && <InfoRow label="Loan ID" value={item.loanId || item.applicationId} last />}
               </div>
             ))}
          </>
        )}

      </div>{/* end scrollable content */}

      {/* ═══ BOTTOM NAVIGATION ═════════════════════ */}
      <div style={{
        flexShrink: 0,
        height: 62,
        background: '#fff',
        display: 'flex',
        borderTop: '1.5px solid #e8eaf6',
        boxShadow: '0 -3px 12px rgba(0,0,0,0.10)',
        zIndex: 10,
      }}>
        {TABS.map(({ key, Icon, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              gap: 3,
              color: tab === key ? '#1d2e77' : '#bbb',
              fontSize: 10,
              fontWeight: 700,
              padding: '4px 0',
              letterSpacing: 0.2,
              transition: 'color 0.15s',
            }}
          >
            <Icon size={tab === key ? 23 : 20} />
            {label}
          </button>
        ))}
      </div>

      {/* ═══ LOGOUT CONFIRM MODAL ══════════════════ */}
      <Modal show={showLogout} onHide={() => setShowLogout(false)} centered>
        <Modal.Body style={{ padding:0, borderRadius:16, overflow:'hidden' }}>
          <div style={{ background:'#ffeaea', padding:'28px 24px 20px', textAlign:'center', borderBottom:'1px solid #ffcccc' }}>
            <div style={{ width:60, height:60, borderRadius:'50%', background:'#e53935', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px', fontSize:22 }}>
              <FaSignOutAlt />
            </div>
            <p style={{ fontWeight:700, fontSize:17, color:'#c62828', margin:0 }}>Logout?</p>
          </div>
          <div style={{ padding:'16px 24px 24px', textAlign:'center' }}>
            <p style={{ fontSize:13, color:'#666', marginBottom:20 }}>
              Are you sure you want to logout from your account?
            </p>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={() => setShowLogout(false)} style={{ flex:1, height:46, background:'#f0f0f0', color:'#444', border:'none', borderRadius:10, fontWeight:700, fontSize:14, cursor:'pointer' }}>
                Cancel
              </button>
              <button onClick={logout} style={{ flex:1, height:46, background:'#e53935', color:'#fff', border:'none', borderRadius:10, fontWeight:700, fontSize:14, cursor:'pointer' }}>
                Yes, Logout
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={processModal} onHide={() => setProcessModal(false)} centered>
        <Modal.Body className="text-center p-4">
          <div style={{ fontSize: 44, marginBottom: 10 }}>⏳</div>
          <p style={{ fontWeight: 700, fontSize: 16, color: '#f57c00', marginBottom: 8 }}>Loan Under Process</p>
          <p style={{ fontSize: 14, color: '#555' }}>
            Your loan is currently under review. Our team will verify and notify you soon whether it is approved or rejected.
          </p>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center">
          <button className="btn btn-warning" style={{ color: '#fff' }} onClick={() => setProcessModal(false)}>OK</button>
        </Modal.Footer>
      </Modal>

      <Modal show={rejectModal} onHide={() => setRejectModal(false)} centered>
        <Modal.Body className="text-center p-4">
          <div style={{ fontSize: 44, marginBottom: 10 }}>❌</div>
          <p style={{ fontWeight: 700, fontSize: 16, color: '#e53935', marginBottom: 8 }}>Loan Rejected</p>
          <p style={{ fontSize: 14, color: '#555' }}>
            This customer's loan application has been rejected. Please contact the branch for more details.
          </p>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center">
          <button className="btn btn-danger" onClick={() => setRejectModal(false)}>OK</button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Home;
